import { Link } from "react-router-dom";
import { LineChart, Factory, ClipboardList, Ship, ArrowRight, CheckCircle2 } from "lucide-react";
import Reveal from "../components/Reveal";
import { Flow, Eyebrow, PrimaryBtn } from "../components/Flow";

const IMG = {
  containers: "https://images.unsplash.com/photo-1494412574643-ff11b0a5c1c3?auto=format&fit=crop&w=1400&q=70",
  interior: "https://images.unsplash.com/photo-1600607687939-ce8a6c25118c?auto=format&fit=crop&w=1400&q=70",
};

const PILLARS = [
  {
    icon: LineChart,
    title: "REAL ESTATE INTELLIGENCE",
    lead: "We help clients evaluate and structure projects before procurement begins.",
    items: ["Project analysis", "Construction budget analysis", "Product specification", "Quantity estimation", "Cost scenarios", "Procurement strategy", "Project optimization"],
    testid: "solutions-pillar-intelligence",
  },
  {
    icon: Factory,
    title: "COLOMBIAN SOURCING",
    lead: "We connect projects with selected Colombian manufacturers and suppliers.",
    items: ["Ceramics", "Porcelain", "Stone", "Sanitaryware", "Faucets", "Doors", "Windows", "Aluminium", "Glass", "Kitchens", "Furniture", "Lighting", "Paint", "Waterproofing", "Construction systems", "Prefabricated solutions"],
    testid: "solutions-pillar-sourcing",
  },
  {
    icon: ClipboardList,
    title: "PROCUREMENT & EXPORT",
    lead: "BUY CARIBBEAN coordinates the procurement process from requirements to export.",
    items: [],
    testid: "solutions-pillar-procurement",
  },
  {
    icon: Ship,
    title: "LOGISTICS & PROJECT SUPPORT",
    lead: "We coordinate the commercial and logistical process between Colombian suppliers and Caribbean projects.",
    items: ["Supplier coordination", "Consolidation", "Export documentation", "International freight coordination", "Shipment tracking", "Delivery coordination", "Project follow-up"],
    note: "Coordination with qualified logistics and local partners when required.",
    testid: "solutions-pillar-logistics",
  },
];

const PACKAGES = [
  { name: "ESSENTIAL", target: "For cost-efficient residential projects.", focus: ["Value", "Reliability", "Standardization", "Optimized procurement"] },
  { name: "PREMIUM", target: "For mid/high-end residential developments.", focus: ["Design", "Quality", "Customization", "Performance"] },
  { name: "CARIBBEAN LUXURY", target: "Villas, resorts, luxury residences and boutique hotels.", focus: ["Premium materials", "Design", "Custom finishes", "Hospitality experience"] },
];

export default function Solutions() {
  return (
    <main data-testid="solutions-page">
      <section className="bg-[#0B132B] py-20 text-white sm:py-24 grain-overlay relative">
        <div className="mx-auto max-w-7xl px-5 lg:px-8">
          <Reveal>
            <Eyebrow dark>Solutions</Eyebrow>
            <h1 className="mt-5 max-w-3xl font-display text-3xl font-extrabold tracking-tight sm:text-4xl lg:text-5xl" data-testid="solutions-headline">
              Real estate intelligence to Caribbean delivery.
            </h1>
            <p className="mt-6 max-w-2xl text-sm leading-relaxed text-white/65 sm:text-base">
              BUY CARIBBEAN operates as your sourcing partner, procurement manager, export partner and logistics coordinator — one accountable process from Colombia to your project site.
            </p>
          </Reveal>
        </div>
      </section>

      <section className="py-20 sm:py-28">
        <div className="mx-auto max-w-7xl space-y-8 px-5 lg:px-8">
          {PILLARS.map((p, i) => (
            <Reveal key={p.title}>
              <div className="grid gap-8 border border-[#0B132B]/12 bg-white p-8 sm:p-12 lg:grid-cols-[auto_1fr_1.4fr]" data-testid={p.testid}>
                <div className="flex items-start gap-5 lg:flex-col lg:gap-4">
                  <span className="font-display text-4xl font-extrabold text-[#0D9488]/25">0{i + 1}</span>
                  <p.icon className="h-8 w-8 text-[#0D9488]" strokeWidth={1.5} />
                </div>
                <div>
                  <h2 className="font-display text-xl font-bold tracking-tight sm:text-2xl">{p.title}</h2>
                  <p className="mt-3 text-sm leading-relaxed text-[#0B132B]/70">{p.lead}</p>
                  {p.note && <p className="mt-4 text-xs italic text-[#0B132B]/50">{p.note}</p>}
                  <Link to="/contact" data-testid={`${p.testid}-cta`} className="mt-6 inline-flex items-center gap-2 text-sm font-bold text-[#0D9488] transition-colors hover:text-[#0B132B]">
                    REQUEST SOURCING <ArrowRight className="h-4 w-4" />
                  </Link>
                </div>
                <div>
                  {p.items.length > 0 && (
                    <div className="flex flex-wrap gap-2">
                      {p.items.map((it) => (
                        <span key={it} className="rounded-full bg-[#0B132B]/[0.05] px-3 py-1.5 text-[11px] font-semibold text-[#0B132B]/70">{it}</span>
                      ))}
                    </div>
                  )}
                  {p.title === "PROCUREMENT & EXPORT" && (
                    <div>
                      <p className="mb-4 font-display text-sm font-semibold text-[#0B132B]/80">
                        From multiple Colombian suppliers to one coordinated procurement process.
                      </p>
                      <Flow
                        vertical
                        testid="solutions-procurement-flow"
                        items={["Project requirements", "Supplier search", "Quotation", "Negotiation", "Quality / Documentation", "Consolidation", "Export", "Caribbean delivery"]}
                      />
                    </div>
                  )}
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      <section className="border-y border-[#0B132B]/10 bg-white py-20 sm:py-24" data-testid="solutions-packages">
        <div className="mx-auto max-w-7xl px-5 lg:px-8">
          <Reveal className="max-w-2xl">
            <Eyebrow>Project Solutions</Eyebrow>
            <h2 className="mt-5 font-display text-2xl font-bold tracking-tight sm:text-3xl lg:text-4xl">
              Procurement packages matched to your ambition.
            </h2>
            <p className="mt-4 text-sm text-[#0B132B]/65">Conceptual packages — scoped and priced per project.</p>
          </Reveal>
          <div className="mt-12 grid gap-6 lg:grid-cols-3">
            {PACKAGES.map((p, i) => (
              <Reveal key={p.name} delay={i * 100}>
                <div className="card-lift h-full border border-[#0B132B]/12 bg-[#F8F7F4] p-8">
                  <h3 className="font-display text-lg font-bold tracking-wide">{p.name}</h3>
                  <p className="mt-2 text-sm text-[#0B132B]/65">{p.target}</p>
                  <ul className="mt-5 space-y-2">
                    {p.focus.map((f) => (
                      <li key={f} className="flex items-center gap-2.5 text-sm text-[#0B132B]/80">
                        <CheckCircle2 className="h-4 w-4 text-[#0D9488]" /> {f}
                      </li>
                    ))}
                  </ul>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="relative overflow-hidden py-20 sm:py-24">
        <img src={IMG.containers} alt="Consolidated cargo ready for Caribbean export" className="hero-img absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0 bg-[#0B132B]/88" />
        <div className="relative mx-auto max-w-4xl px-5 text-center lg:px-8">
          <Reveal>
            <h2 className="font-display text-2xl font-bold tracking-tight text-white sm:text-3xl">
              Ready to structure your procurement?
            </h2>
            <div className="mt-8 flex flex-wrap items-center justify-center gap-4">
              <PrimaryBtn to="/contact" testid="solutions-cta-submit" light>SUBMIT YOUR PROJECT</PrimaryBtn>
            </div>
          </Reveal>
        </div>
      </section>
    </main>
  );
}
